import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './WettkampftagChecklist.css';

// Minimaler Print-Helper (analog DruckDashboard)
const printCSS = `
  body { font-family: Arial, sans-serif; font-size: 9pt; color: #111; margin: 0; padding: 0; }
  h2 { font-size: 12pt; margin: 0 0 4mm; }
  .division { margin-bottom: 6mm; page-break-inside: avoid; }
  .division-title { font-size: 10pt; font-weight: 700; border-bottom: 0.5pt solid #aaa; padding-bottom: 1mm; margin-bottom: 2mm; }
  table { width: 100%; border-collapse: collapse; font-size: 8pt; }
  th { background: #eee; text-align: left; padding: 1mm 2mm; border: 0.3pt solid #ccc; }
  td { padding: 1mm 2mm; border: 0.3pt solid #ccc; }
  tr:nth-child(even) td { background: #f9f9f9; }
  .header { display: flex; justify-content: space-between; margin-bottom: 5mm; border-bottom: 1pt solid #333; padding-bottom: 2mm; }
  @media print { body { margin: 10mm; } }
`;

function printWindow(html) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

function buildStartlistenHTML(data) {
  const { turnier, divisionen } = data;
  const divHTML = divisionen.map(div => {
    const rows = (div.wettkaempfer || []).map((w, i) => `
      <tr>
        <td style="width:8mm;text-align:center">${i + 1}</td>
        <td><strong>${w.nachname}</strong>, ${w.vorname}</td>
        <td>${w.verein_name || '—'}</td>
        <td style="width:20mm">${w.jahrgang || '—'}</td>
        ${div.typ === 'kumite' ? `<td style="width:18mm">${w.gewicht ? w.gewicht + ' kg' : '—'}</td>` : ''}
      </tr>
    `).join('');
    return `
      <div class="division">
        <div class="division-title">${div.name}${div.division_code ? ' · ' + div.division_code : ''} (${(div.wettkaempfer || []).length} Starter)</div>
        <table>
          <thead><tr>
            <th>#</th><th>Athlet</th><th>Verein</th><th>Jg.</th>
            ${div.typ === 'kumite' ? '<th>Gewicht</th>' : ''}
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }).join('');

  return `<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8">
    <title>Starterlisten — ${turnier?.name || ''}</title>
    <style>${printCSS}</style></head>
  <body>
    <div class="header">
      <div>
        <strong>${turnier?.name || 'Turnier'}</strong><br>
        <span style="font-size:8pt;color:#555">${turnier?.ort || ''}</span>
      </div>
      <div style="text-align:right;font-size:8pt;color:#555">
        STARTERLISTEN<br>Stand: ${new Date().toLocaleString('de-DE')}
      </div>
    </div>
    ${divHTML}
    <script>window.onload = function() { window.focus(); window.print(); };</script>
  </body></html>`;
}

const CHECKLIST_ITEMS = [
  {
    id: 'anmeldungen',
    icon: '📋',
    title: 'Anmeldungen prüfen & freigeben',
    description: 'Alle eingegangenen Anmeldungen prüfen, bestätigen oder ablehnen.',
    view: 'anmeldungen'
  },
  {
    id: 'einwaage',
    icon: '⚖️',
    title: 'Einwaage durchführen',
    description: 'Gewichtskontrolle für alle Kämpfer durchführen.',
    view: 'einwaage'
  },
  {
    id: 'starterlisten',
    icon: '🖨️',
    title: 'Starterlisten drucken',
    description: 'Starterlisten für alle Divisionen ausdrucken und verteilen.',
    view: 'druck',
    directAction: { label: 'Alle drucken', icon: '🖨️', type: 'print_starterlisten' }
  },
  {
    id: 'brackets',
    icon: '🥊',
    title: 'Brackets erstellen',
    description: 'Kampfaufteilung und KO-Brackets für alle Divisionen generieren.',
    view: 'brackets',
    directAction: { label: 'Auto-generieren', icon: '⚡', type: 'auto_brackets' }
  },
  {
    id: 'formen',
    icon: '🎋',
    title: 'Formen-Starts erstellen',
    description: 'Formen-Kategorien anlegen und Teilnehmer den Starts zuweisen.',
    view: 'formen',
    directAction: { label: 'Auto-generieren', icon: '⚡', type: 'auto_formen' }
  },
  {
    id: 'kampfflaechen',
    icon: '🟦',
    title: 'Mattenflächen einrichten',
    description: 'Tatamis und Kampfflächen konfigurieren und Disziplinen zuweisen.',
    view: 'kampfflaechen',
    directAction: { label: 'Auto-generieren', icon: '⚡', type: 'auto_kampfflaechen' }
  },
  {
    id: 'zeitplan',
    icon: '⏰',
    title: 'Zeitplan erstellen',
    description: 'Ablaufplan erstellen und Tatamis mit Zeiten und Disziplinen belegen.',
    view: 'zeitplan',
    directAction: { label: 'Generieren', icon: '⚡', type: 'navigate_zeitplan' }
  },
  {
    id: 'kampfrichter',
    icon: '👨‍⚖️',
    title: 'Kampfrichter zuteilen',
    description: 'Kampfrichter anlegen und den Tatamis und Kategorien zuweisen.',
    view: 'staff-management'
  },
  {
    id: 'streaming',
    icon: '📡',
    title: 'Streaming vorbereiten',
    description: 'Live-Stream einrichten und Übertragungseinstellungen konfigurieren.',
    view: 'streaming'
  },
  {
    id: 'aufruf',
    icon: '📢',
    title: 'Aufruf-Display starten',
    description: 'Kampfaufruf-Display für Athleten und Zuschauer aktivieren.',
    view: null
  },
  {
    id: 'ansage',
    icon: '🔊',
    title: 'Ansage-Station starten',
    description: 'Rechner an der Lautsprecheranlage: spricht die Aufrufe automatisch.',
    view: null,
    directAction: { label: 'Station öffnen', icon: '🔊', type: 'ansage_station' }
  }
];

function WettkampftagChecklist({ isOpen, onClose, selectedTurnier, setActiveView }) {
  const turnierId = selectedTurnier?.turnier_id || selectedTurnier?.id || 'global';
  const storageKey = `wettkampftag_checklist_${turnierId}`;

  const [checked, setChecked] = useState({});
  const [actionLoading, setActionLoading] = useState({});
  const [actionMsg, setActionMsg] = useState({});
  // Welche Klassen kein Bracket bekommen haben — je Prüfpunkt.
  const [uebersprungen, setUebersprungen] = useState({});
  // Zustand der Sprachansage: haengt ein Rechner an der Anlage, ist der Ton
  // eingerichtet, wie viele Ansagen warten?
  const [ansage, setAnsage] = useState(null);
  const [schalterLaeuft, setSchalterLaeuft] = useState(false);

  useEffect(() => {
    if (isOpen) {
      try {
        const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
        setChecked(saved);
      } catch {
        setChecked({});
      }
      setActionMsg({});
    }
  }, [isOpen, storageKey]);

  // ⚠️ Solange die Liste offen ist, alle fuenf Sekunden nachsehen. Ob eine
  // Station verbunden ist, aendert sich genau dann, wenn jemand den Laptop
  // an der Anlage auf- oder zumacht — und das passiert waehrend man hier
  // steht.
  useEffect(() => {
    if (!isOpen || !selectedTurnier) return undefined;
    const tid = selectedTurnier.turnier_id || selectedTurnier.id;
    let laeuft = true;
    const laden = async () => {
      try {
        const r = await fetch(`/api/ansagen/turnier/${tid}/bereit`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const d = await r.json();
        if (laeuft && d.success) setAnsage(d);
      } catch { /* der naechste Durchlauf versucht es wieder */ }
    };
    laden();
    const i = setInterval(laden, 5000);
    return () => { laeuft = false; clearInterval(i); };
  }, [isOpen, selectedTurnier]);

  const ansageSchalten = useCallback(async (aktiv) => {
    if (!selectedTurnier) return;
    const tid = selectedTurnier.turnier_id || selectedTurnier.id;
    setSchalterLaeuft(true);
    // Sofort umschalten, damit der Haken nicht erst nach dem Server springt —
    // am Turniertag klickt sonst jemand ein zweites Mal.
    setAnsage((a) => (a ? { ...a, eingeschaltet: aktiv } : a));
    try {
      const r = await fetch(`/api/ansagen/turnier/${tid}/schalter`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ aktiv }),
      });
      const d = await r.json();
      if (!d.success) {
        // Zuruecknehmen: sonst zeigt die Liste einen Zustand, den es nicht gibt.
        setAnsage((a) => (a ? { ...a, eingeschaltet: !aktiv } : a));
      }
    } catch {
      setAnsage((a) => (a ? { ...a, eingeschaltet: !aktiv } : a));
    } finally {
      setSchalterLaeuft(false);
    }
  }, [selectedTurnier]);

  const toggleItem = useCallback((id) => {
    setChecked(prev => {
      const next = { ...prev, [id]: !prev[id] };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }, [storageKey]);

  const handleNavigate = useCallback((item) => {
    if (item.view) {
      setActiveView(item.view);
      onClose();
    } else if (item.id === 'aufruf' && selectedTurnier) {
      const tid = selectedTurnier.turnier_id || selectedTurnier.id;
      window.open(`/aufruf/${tid}`, '_blank');
    }
  }, [setActiveView, onClose, selectedTurnier]);

  const handleDirectAction = useCallback(async (item) => {
    if (!selectedTurnier) return;
    const tid = selectedTurnier.turnier_id || selectedTurnier.id;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    setActionLoading(prev => ({ ...prev, [item.id]: true }));
    setActionMsg(prev => ({ ...prev, [item.id]: '' }));

    try {
      if (item.directAction.type === 'print_starterlisten') {
        const r = await fetch(`/api/druck/${tid}/startlisten`, { headers });
        const d = await r.json();
        if (d.success && d.divisionen?.length > 0) {
          printWindow(buildStartlistenHTML(d));
          setActionMsg(prev => ({ ...prev, [item.id]: `✓ ${d.divisionen.length} Divisionen` }));
        } else {
          setActionMsg(prev => ({ ...prev, [item.id]: 'Keine Daten vorhanden' }));
        }
      } else if (item.directAction.type === 'auto_brackets') {
        const r = await fetch(`/api/brackets/turnier/${tid}/auto-generate`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }
        });
        const d = await r.json();
        // ⚠️ Hier stand nur „✓ Brackets generiert". Der Server meldet aber
        // MIT, welche Klassen er uebersprungen hat und warum -- zum Beispiel
        // „Weniger als 2 Teilnehmer". Das wurde verworfen.
        //
        // Gemessen am 16.09.2026 an Turnier 3: acht Divisionen hatten genau
        // einen Angemeldeten und bekamen kein Bracket. Der Veranstalter sah
        // einen gruenen Haken und erfuhr nichts davon -- diese Leute stehen in
        // keiner Startliste und haetten am Wettkampftag danebengestanden.
        //
        // Punkte-Kategorien (Formen, Bruchtest) werden getrennt gezaehlt: dass
        // sie kein Bracket bekommen, ist normal und keine Warnung wert.
        if (!d.success) {
          setActionMsg(prev => ({ ...prev, [item.id]: d.error || d.message || 'Fehler' }));
        } else {
          const z = d.data || {};
          const teile = [`✓ ${z.brackets_erstellt ?? 0} Brackets`];
          if (z.punkte_kategorien > 0) teile.push(`${z.punkte_kategorien} Punkte-Kategorien`);
          if (z.klassen_uebersprungen > 0) {
            const gruende = [...new Set((z.uebersprungen || []).map(k => k.grund))];
            teile.push(`⚠️ ${z.klassen_uebersprungen} ohne Bracket`
              + (gruende.length ? ` (${gruende.join(', ')})` : ''));
          }
          setActionMsg(prev => ({ ...prev, [item.id]: teile.join(' · ') }));
          // Die betroffenen Klassen einzeln, damit man weiss, wen es trifft.
          setUebersprungen(prev => ({ ...prev, [item.id]: z.uebersprungen || [] }));
        }
      } else if (item.directAction.type === 'auto_formen') {
        const r = await fetch(`/api/formen/auto-generate/${tid}`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }
        });
        const d = await r.json();
        setActionMsg(prev => ({ ...prev, [item.id]: d.success ? '✓ Formen generiert' : (d.error || 'Fehler') }));
      } else if (item.directAction.type === 'auto_kampfflaechen') {
        const r = await fetch(`/api/kampfflaechen/${tid}/auto-generate`, {
          method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }
        });
        const d = await r.json();
        setActionMsg(prev => ({ ...prev, [item.id]: d.success ? '✓ Flächen generiert' : (d.error || 'Fehler') }));
      } else if (item.directAction.type === 'ansage_station') {
        // Eigenes Fenster: die Station laeuft den ganzen Tag und darf nicht
        // verschwinden, weil jemand im Dashboard weiterklickt.
        window.open(`/ansage/${tid}`, '_blank');
        setActionMsg(prev => ({ ...prev, [item.id]: 'Station in neuem Fenster geöffnet' }));
      } else if (item.directAction.type === 'navigate_zeitplan') {
        setActiveView('zeitplan');
        onClose();
        return;
      }
    } catch (e) {
      setActionMsg(prev => ({ ...prev, [item.id]: 'Fehler aufgetreten' }));
    }
    setActionLoading(prev => ({ ...prev, [item.id]: false }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onClose und setActiveView kommen vom Eltern-Element und wechseln bei jedem Rendern die Kennung
  }, [selectedTurnier]);

  const resetAll = useCallback(() => {
    localStorage.removeItem(storageKey);
    setChecked({});
    setActionMsg({});
    setUebersprungen({});
  }, [storageKey]);

  if (!isOpen) return null;

  const open = CHECKLIST_ITEMS.filter(i => !checked[i.id]);
  const done = CHECKLIST_ITEMS.filter(i => checked[i.id]);
  const sorted = [...open, ...done];
  const doneCount = done.length;
  const total = CHECKLIST_ITEMS.length;
  const progress = Math.round((doneCount / total) * 100);

  return ReactDOM.createPortal(
    <div className="wkt-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="wkt-modal">
        <div className="wkt-header">
          <div className="wkt-title-row">
            <div className="wkt-title-group">
              <span className="wkt-title-icon">🏅</span>
              <div>
                <h2>Wettkampftag-Vorbereitung</h2>
                {selectedTurnier && (
                  <p className="wkt-turnier-name">{selectedTurnier.name}</p>
                )}
              </div>
            </div>
            <button className="wkt-close-btn" onClick={onClose} aria-label="Schließen">✕</button>
          </div>

          <div className="wkt-progress-row">
            <div className="wkt-progress-bar">
              <div className="wkt-progress-fill" style={{ width: `${progress}%` }} />
            </div>
            <span className="wkt-progress-label">{doneCount} / {total} erledigt</span>
          </div>
        </div>

        <div className="wkt-list">
          {sorted.map((item, idx) => {
            const isDone = !!checked[item.id];
            const isFirstDone = idx > 0 && !checked[sorted[idx - 1]?.id] && isDone;
            const loading = actionLoading[item.id];
            const msg = actionMsg[item.id];

            return (
              <React.Fragment key={item.id}>
                {isFirstDone && doneCount > 0 && open.length > 0 && (
                  <div className="wkt-divider"><span>Erledigt</span></div>
                )}
                <div className={`wkt-item ${isDone ? 'wkt-item--done' : ''}`}>
                  <button
                    className={`wkt-checkbox ${isDone ? 'wkt-checkbox--checked' : ''}`}
                    onClick={() => toggleItem(item.id)}
                    aria-label={isDone ? 'Als offen markieren' : 'Als erledigt markieren'}
                  >
                    {isDone ? '✓' : ''}
                  </button>

                  <div className="wkt-item-icon">{item.icon}</div>

                  <div className="wkt-item-content">
                    <span className="wkt-item-title">{item.title}</span>
                    <span className="wkt-item-desc">{item.description}</span>
                    {msg && (
                      <span className={`wkt-action-msg ${msg.startsWith('✓') ? 'wkt-action-msg--ok' : 'wkt-action-msg--err'}`}>
                        {msg}
                      </span>
                    )}
                    {/* Welche Klassen kein Bracket bekommen haben. Ohne diese
                        Liste weiss man zwar, DASS etwas fehlt, aber nicht, wen
                        es trifft. */}
                    {(uebersprungen[item.id] || []).length > 0 && (
                      <ul className="wkt-uebersprungen">
                        {uebersprungen[item.id].map((k) => (
                          <li key={k.division_code}>
                            <strong>{k.division_code}</strong>
                            {k.teilnehmer_count != null && ` · ${k.teilnehmer_count} `}
                            {k.teilnehmer_count === 1 ? 'Teilnehmer' : k.teilnehmer_count != null ? 'Teilnehmer' : ''}
                            {k.grund ? ` — ${k.grund}` : ''}
                          </li>
                        ))}
                      </ul>
                    )}

                    {/* ⚠️ Zustand der Sprachansage — die einzige Zeile der
                        Liste, die etwas ueber JETZT sagt statt ueber eine
                        erledigte Vorbereitung.

                        Der Grund: Eine Warteschlange, die waechst, weil kein
                        Rechner an der Anlage haengt, sieht von innen genauso
                        aus wie eine, die gut laeuft. Man merkt es erst, wenn
                        jemand nicht aufgerufen wurde. */}
                    {item.id === 'ansage' && ansage && (
                      <div className="wkt-ansage">
                        {/* ⚠️ Der Schalter gehoert HIERHIN, nicht nur ins
                            Bearbeiten-Formular. Am Turniertag steht jemand vor
                            dieser Liste; ihn in ein Formular mit Pflichtfeldern
                            und Speichern-Knopf zu schicken, wo er nebenbei
                            etwas anderes verstellen kann, waere der falsche Weg. */}
                        <label className="wkt-ansage-schalter">
                          <input
                            type="checkbox"
                            checked={!!ansage.eingeschaltet}
                            disabled={schalterLaeuft}
                            onChange={(e) => ansageSchalten(e.target.checked)}
                          />
                          Automatische Ansage {ansage.eingeschaltet ? 'ein' : 'aus'}
                          {schalterLaeuft && ' …'}
                        </label>
                        {!ansage.eingeschaltet && (
                          <span className="wkt-ansage-warn">
                            Es werden keine Aufrufe gesprochen.
                          </span>
                        )}
                        {ansage.eingeschaltet && !ansage.station_verbunden && (
                          <span className="wkt-ansage-warn">
                            ⚠️ Keine Station verbunden — es wird nichts gesprochen
                            {ansage.warten > 0 && ` (${ansage.warten} Ansage(n) warten)`}
                          </span>
                        )}
                        {ansage.eingeschaltet && ansage.station_verbunden && (
                          <span className="wkt-ansage-ok">
                            ✓ Station verbunden
                            {ansage.warten > 0 ? ` · ${ansage.warten} in der Warteschlange` : ' · nichts offen'}
                          </span>
                        )}
                        {!ansage.ton_bereit && (
                          <span className="wkt-ansage-warn">
                            ⚠️ Sprachausgabe auf dem Server nicht eingerichtet — es
                            spricht die Browserstimme
                          </span>
                        )}
                        {ansage.ton_bereit && ansage.stimme_de && (
                          <span className="wkt-ansage-leise">
                            {ansage.stimme_de}
                            {ansage.zweisprachig && ansage.stimme_en ? ` + ${ansage.stimme_en}` : ' (nur Deutsch)'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="wkt-item-actions">
                    {item.directAction && (
                      <button
                        className="wkt-direct-btn"
                        onClick={() => handleDirectAction(item)}
                        disabled={!!loading}
                        title={item.directAction.label}
                      >
                        <span>{loading ? '⏳' : item.directAction.icon}</span>
                        <span>{item.directAction.label}</span>
                      </button>
                    )}
                    <button
                      className={`wkt-open-btn ${isDone ? 'wkt-open-btn--secondary' : ''}`}
                      onClick={() => handleNavigate(item)}
                      title="Öffnen"
                    >
                      →
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        {doneCount > 0 && (
          <div className="wkt-footer">
            <button className="wkt-reset-btn" onClick={resetAll}>Alle zurücksetzen</button>
            {doneCount === total && (
              <span className="wkt-all-done">🎉 Alles vorbereitet — viel Erfolg!</span>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default WettkampftagChecklist;
